import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, adminProductService } from '../services/adminService';
import SessionTimeout from '../components/SessionTimeout';
import './styles/AdminDashboard.css';

interface Product {
  productId: string;
  name: string;
  price: number;
  category: string;
  tags: string[];
  active: boolean;
  inventory: number;
}

const AdminDashboard: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminProductService.getProducts();
      setProducts(data);
    } catch (err: any) {
      setError(err.message);
      if (err.message.includes('Unauthorized')) {
        authService.logout();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleDelete = async (productId: string) => {
    try {
      await adminProductService.deleteProduct(productId);
      await loadProducts();
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <SessionTimeout />
      
      <div className="admin-header">
        <div className="header-left">
          <h1 className="admin-title">Product Management</h1>
          <p className="admin-subtitle">{products.length} products total</p>
        </div>
        <div className="header-right">
          <button className="btn-secondary" onClick={() => navigate('/')}>
            ← Back to Store
          </button>
          <button className="btn-primary" onClick={() => navigate('/admin/products/new')}>
            + Add Product
          </button>
          <button className="btn-secondary" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
          <button className="dismiss-btn" onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="products-table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>Price</th>
              <th>Inventory</th>
              <th>Tags</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.productId} className={!product.active ? 'inactive-row' : ''}>
                <td>
                  <div className="product-cell">
                    <span className="product-id">{product.productId}</span>
                    <span className="product-name">{product.name}</span>
                  </div>
                </td>
                <td>{product.category}</td>
                <td className="price-cell">${product.price.toFixed(2)}</td>
                <td className="inventory-cell">
                  <span className={product.inventory < 5 ? 'low-stock' : ''}>
                    {product.inventory}
                  </span>
                </td>
                <td>
                  <div className="tags-cell">
                    {product.tags?.map((tag) => (
                      <span key={tag} className="tag-badge">{tag}</span>
                    ))}
                    {(!product.tags || product.tags.length === 0) && (
                      <span className="no-tags">No tags</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${product.active ? 'active' : 'inactive'}`}>
                    {product.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => navigate(`/admin/products/edit/${product.productId}`)}
                      title="Edit"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {deleteConfirm === product.productId ? (
                      <div className="confirm-delete">
                        <button
                          className="btn-icon btn-confirm"
                          onClick={() => handleDelete(product.productId)}
                          title="Confirm"
                        >
                          ✓
                        </button>
                        <button
                          className="btn-icon btn-cancel"
                          onClick={() => setDeleteConfirm(null)}
                          title="Cancel"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => setDeleteConfirm(product.productId)}
                        title="Delete"
                        disabled={!product.active}
                      >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {products.length === 0 && (
          <div className="empty-state">
            <p>No products found</p>
            <button className="btn-primary" onClick={() => navigate('/admin/products/new')}>
              Add Your First Product
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;